import React from 'react';
import { motion } from 'framer-motion';
import { cadenceOptions, hmdaByReason, lenderNames, loanHeader } from '../../data/withdraw';
import { DeadForm, RecoveryPath, SeasoningForm } from '../../types/withdraw';
import { firstContactDate, formatDate } from '../../utils/dates';
import { easeOut } from '../../utils/styles';
import { isReApp } from '../../utils/withdrawValidation';
import { newLoanNumber, originalLoanDocuments } from '../../data/reApp';
import { PillButton } from '../ui/PillButton';
import { Confetti } from './Confetti';
interface SuccessStepProps {
  path: RecoveryPath;
  dead: DeadForm;
  seasoning: SeasoningForm;
  onDone: () => void;
}
export function SuccessStep({
  path,
  dead,
  seasoning,
  onDone
}: SuccessStepProps) {
  const rows = path === 'dead' ? deadRows(dead) : seasoningRows(seasoning);
  const cadence = cadenceOptions.find(c => c.id === seasoning.cadence) ?? cadenceOptions[1];
  const reApp = path === 'dead' && isReApp(dead);
  return <div className="flex flex-col items-center py-2 text-center" data-id="element-217" data-mp="ox5axf2tgseb">
      <div className="relative grid h-28 w-28 place-items-center" data-id="element-218" data-mp="oxnj9a2r6x2l">
        <Confetti data-id="element-219" data-mp="sxfslozz1790" />
        <motion.div initial={{
        scale: 0.96,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 0.25,
        ease: easeOut
      }} className="grid h-24 w-24 place-items-center rounded-full bg-primary-50 ring-8 ring-primary-50/60" data-id="element-220" data-mp="sxxo2jet7hfv">
          <svg viewBox="0 0 52 52" className="h-16 w-16 text-primary-700" aria-hidden="true" data-id="element-221" data-mp="583c042bdwwo">
            <motion.circle cx="26" cy="26" r="23" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" initial={{
            pathLength: 0
          }} animate={{
            pathLength: 1
          }} transition={{
            duration: 0.3,
            ease: easeOut
          }} data-id="element-222" data-mp="a0ek3u77kp0b" />
            <motion.path d="M15.5 27 L22.5 34 L37 19.5" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" initial={{
            pathLength: 0
          }} animate={{
            pathLength: 1
          }} transition={{
            duration: 0.28,
            delay: 0.25,
            ease: easeOut
          }} data-id="element-223" data-mp="3nerern9w67z6" />
          </svg>
        </motion.div>
      </div>

      <h3 className="mt-4 text-2xl font-bold tracking-tight text-slate-900" data-id="element-224" data-mp="2pj5j2eqh1yl2">
        {path === 'dead' ? reApp ? 'Withdrawn and re-apped' : 'Loan withdrawn' : 'Enrolled in Loan Seasoning'}
      </h3>
      <p className="mt-1.5 max-w-md text-sm text-slate-500" data-id="element-225" data-mp="3i7taks1tdk72">
        {path === 'dead' ? reApp ? `New loan ${newLoanNumber} with ${dead.newLender} is ready, with ${loanHeader.borrower}'s details brought over.` : `${loanHeader.borrower}'s file is out of your active pipeline. Thanks for keeping reporting tidy.` : `We'll nudge you ${cadence.phrase} until ${loanHeader.borrower.split(' ')[0]}'s loan is workable again.`}
      </p>

      <motion.dl initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.25,
      delay: 0.35,
      ease: easeOut
    }} className="mt-6 w-full max-w-[560px] divide-y divide-slate-100 rounded-2xl bg-white text-left shadow-soft ring-1 ring-slate-200" data-id="element-226" data-mp="cfv6lla0xxif">
        {rows.map(([label, value]) => <div key={label} className="flex items-start justify-between gap-6 px-5 py-3 text-sm" data-id="element-227" data-mp="w2jagnfujgsi">
            <dt className="shrink-0 text-slate-500" data-id="element-228" data-mp="103l66uak1apq">{label}</dt>
            <dd className="text-right font-semibold text-slate-900" data-id="element-229" data-mp="105xv5reqclca">{value}</dd>
          </div>)}
      </motion.dl>

      <div className="mt-6 flex items-center justify-center" data-id="element-230" data-mp="1a20smjymzvsw">
        <PillButton onClick={onDone} data-id="element-231" data-mp="1a19bge5se0m5">Done</PillButton>
      </div>
    </div>;
}
function deadRows(dead: DeadForm): [string, string][] {
  const sync = dead.lenderVariant === 'sync';
  const hmda = dead.reason ? hmdaByReason[dead.reason] : null;
  const rows: [string, string][] = [['Path', 'Withdraw (Dead)'], ['Reason', dead.reason || '—'], ['Withdraw date', formatDate(dead.withdrawDate)], ['HMDA', sync ? `Reported via ${lenderNames.sync} sync` : hmda ? `${hmda.code} – ${hmda.label}` : '—'], ['Lender status', sync ? `Withdrawn with ${lenderNames.sync}: Pending` : `Withdrawn with ${lenderNames.nonsync} on ${formatDate(dead.lenderWithdrawDate)}`]];
  if (isReApp(dead)) {
    rows.push(['New loan', `${newLoanNumber} · ${dead.newLender}`]);
    rows.push(['Documents brought over', `${dead.carryDocs.length} of ${originalLoanDocuments.length}`]);
  }
  if (dead.notes) rows.push(['Notes', dead.notes]);
  return rows;
}
function seasoningRows(s: SeasoningForm): [string, string][] {
  const cadence = cadenceOptions.find(c => c.id === s.cadence) ?? cadenceOptions[1];
  const rows: [string, string][] = [['Path', 'Loan Seasoning (Workable)'], ['Reason', s.reason || '—']];
  if (s.reason === 'Rate Watch') rows.push(['Current → target rate', `${s.currentRate}% → ${s.targetRate}%`]);
  if (s.reason === 'Credit Repair') rows.push(['Current → target score', `${s.currentScore} → ${s.targetScore}`]);
  if (s.reason === 'Forbearance' || s.reason === 'Loan Modification') rows.push(['Expected end', formatDate(s.expectedEndDate)]);
  if (s.reason === 'Loan Seasoning') {
    rows.push(['Seasoning type', s.seasoningType]);
    rows.push(['Eligible date', formatDate(s.eligibleDate)]);
  }
  const first = cadence.phrase.charAt(0).toUpperCase() + cadence.phrase.slice(1);
  rows.push(['Next contact', `${first}, first on ${formatDate(firstContactDate(s.cadence), 'MMM d')}`]);
  rows.push(['Suspended until', formatDate(s.suspendedUntil)]);
  return rows;
}